import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  loyaltyApi,
  BURN_RULE_LABELS,
  LOYALTY_REDEEM_HOLD_MESSAGE,
  type LoyaltyBurnPurpose,
} from '@/api/loyalty';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Gift, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BURN_PURPOSES: LoyaltyBurnPurpose[] = [
  'OPD_DISCOUNT',
  'PHARMACY_DISCOUNT',
  'FREE_CONSULTATION',
  'DIAGNOSTICS',
  'IPD_DISCOUNT',
  'DAY_CARE_DISCOUNT',
  'MEMBERSHIP_UPGRADE',
];

export function PatientLoyaltyCard({ patientId }: { patientId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [redeemPoints, setRedeemPoints] = useState('');
  const [burnPurpose, setBurnPurpose] = useState<LoyaltyBurnPurpose>('OPD_DISCOUNT');
  const [maxRedeem, setMaxRedeem] = useState<number | null>(null);

  const canRedeem =
    user?.role === 'RECEPTIONIST' ||
    user?.role === 'PHARMACIST' ||
    user?.role === 'HOSPITAL_ADMIN' ||
    user?.role === 'SUPER_ADMIN';

  const { data, isLoading } = useQuery({
    queryKey: ['loyalty-patient', patientId],
    queryFn: () => loyaltyApi.getPatientSummary(patientId),
  });

  useEffect(() => {
    if (!canRedeem || !data?.loyaltyEnabled) return;
    loyaltyApi
      .getRedemptionQuote(patientId, burnPurpose)
      .then((q) => setMaxRedeem(q.maxPoints))
      .catch(() => setMaxRedeem(null));
  }, [patientId, burnPurpose, data?.loyaltyEnabled, canRedeem]);

  const redeemMutation = useMutation({
    mutationFn: (pts: number) =>
      loyaltyApi.redeemPoints(patientId, pts, { purpose: burnPurpose }),
    onSuccess: (res) => {
      const extra = res.membershipTierName
        ? ` — upgraded to ${res.membershipTierName}`
        : '';
      toast.success(`Redeemed ${res.pointsBurned} points (₹${res.rupeeValue})${extra}`);
      setRedeemPoints('');
      qc.invalidateQueries({ queryKey: ['loyalty-patient', patientId] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message || 'Redemption failed');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-4">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading loyalty…
      </div>
    );
  }

  if (!data?.loyaltyEnabled) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        Loyalty program is not enabled for this hospital.
      </p>
    );
  }

  const activeBurn = data.burnRules?.filter((r) => r.isActive) ?? [];

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-gradient-to-br from-teal-50/80 to-white">
      <div className="flex items-center gap-2">
        <Gift className="h-5 w-5 text-teal-700" />
        <h3 className="font-semibold text-lg">Loyalty & wallet</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <p className="text-muted-foreground">Points{data.familyPoolingEnabled ? ' (family pool)' : ''}</p>
          <p className="text-xl font-bold text-teal-800">
            {data.redeemableBalance ?? data.pointsBalance}
            {(data.pointsOnHold ?? 0) > 0 ? (
              <span className="text-sm font-normal text-muted-foreground">
                {' '}
                / {data.pointsBalance} total
              </span>
            ) : null}
          </p>
          <p className="text-xs text-muted-foreground">≈ ₹{data.rupeeValue?.toFixed(2)}</p>
          {data.familyPoolingEnabled && data.ownPointsBalance != null && (
            <p className="text-xs text-muted-foreground">Your wallet: {data.ownPointsBalance}</p>
          )}
        </div>
        {data.membership && (
          <div>
            <p className="text-muted-foreground">Tier</p>
            <p className="font-semibold">{data.membership.displayName}</p>
          </div>
        )}
        <div>
          <p className="text-muted-foreground">Cashback</p>
          <p className="font-medium">₹{data.cashbackBalance?.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Promo credits</p>
          <p className="font-medium">₹{data.promoBalance?.toFixed(2)}</p>
        </div>
      </div>

      <p className="text-xs text-amber-900 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2">
        {data.redeemHoldMessage ?? LOYALTY_REDEEM_HOLD_MESSAGE}
        {(data.pointsOnHold ?? 0) > 0 ? ` ${data.pointsOnHold} points on hold.` : ''}
      </p>

      {(data.cashbackBalance > 0 || data.promoBalance > 0 || data.refundBalance > 0) && (
        <p className="text-xs text-muted-foreground">
          Wallet credits can be applied at payment (cashback / promo / refund).
        </p>
      )}

      {canRedeem && (
        <p className="text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded-md px-2.5 py-2">
          <strong>At checkout:</strong> open the patient&apos;s bill in billing / queue / pharmacy
          sale — loyalty points can be applied there and the bill total updates automatically.
        </p>
      )}

      {canRedeem && (
        <div className="flex flex-wrap items-end gap-3 pt-2 border-t">
          <p className="w-full text-xs text-muted-foreground">
            Manual redemption (no bill linked) — prefer applying on the open bill at checkout.
          </p>
          <div className="space-y-1">
            <Label>Redemption type</Label>
            <select
              className="border rounded-md px-2 py-2 text-sm min-w-[180px]"
              value={burnPurpose}
              onChange={(e) => setBurnPurpose(e.target.value as LoyaltyBurnPurpose)}
            >
              {BURN_PURPOSES.map((p) => {
                const rule = activeBurn.find((r) => r.purpose === p);
                if (activeBurn.length > 0 && !rule) return null;
                return (
                  <option key={p} value={p}>
                    {BURN_RULE_LABELS[p]}
                    {rule?.pointsCost ? ` (${rule.pointsCost} pts)` : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="redeem-pts">Points to burn</Label>
            <Input
              id="redeem-pts"
              type="number"
              min={1}
              max={maxRedeem ?? undefined}
              value={redeemPoints}
              onChange={(e) => setRedeemPoints(e.target.value)}
              className="w-32"
            />
            {maxRedeem != null && (
              <p className="text-xs text-muted-foreground">Max: {maxRedeem} pts</p>
            )}
          </div>
          <Button
            type="button"
            disabled={redeemMutation.isPending || !redeemPoints}
            onClick={() => redeemMutation.mutate(Number(redeemPoints))}
          >
            {redeemMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Redeem'}
          </Button>
        </div>
      )}

      {data.recentTransactions?.length > 0 && (
        <div className="text-xs space-y-1 max-h-32 overflow-y-auto">
          <p className="font-medium text-muted-foreground">Recent activity</p>
          {data.recentTransactions.slice(0, 5).map((tx) => (
            <div key={tx.id} className="flex justify-between gap-2">
              <span className="truncate">
                {tx.description || tx.burnPurpose || tx.activityType}
              </span>
              <span className={tx.type === 'EARN' ? 'text-teal-700' : 'text-orange-700'}>
                {tx.type === 'EARN' ? '+' : '-'}
                {tx.points}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
